import { useEffect, useState, useRef } from "react";
import { Loader2, Upload, FileText, Image as ImageIcon, Download, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { API, formatApiErrorDetail } from "@/lib/api";
import { useLang } from "@/i18n/LanguageContext";

const fmtSize = (b) => (b > 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);
const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" }); } catch { return ""; } };

export function CreatorDocuments({ creatorId }) {
  const { t } = useLang();
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get(`/admin/creators/${creatorId}/documents`); setDocs(data); }
    catch { /* ignore */ } finally { setLoading(false); }
  };
  useEffect(() => { if (creatorId) load(); /* eslint-disable-next-line */ }, [creatorId]);

  const onPick = () => fileRef.current?.click();

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const form = new FormData();
    form.append("file", file);
    setUploading(true);
    try {
      const { data } = await api.post(`/admin/creators/${creatorId}/documents`, form, { headers: { "Content-Type": "multipart/form-data" } });
      setDocs((arr) => [data, ...arr]);
      toast.success(t("docs.uploaded"));
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setUploading(false); }
  };

  const remove = async (id) => {
    if (!window.confirm(t("docs.deleteConfirm"))) return;
    try { await api.delete(`/admin/documents/${id}`); setDocs((arr) => arr.filter((d) => d.id !== id)); toast.success(t("docs.deleted")); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const downloadUrl = (id) => `${API}/admin/documents/${id}/download?auth=${encodeURIComponent(localStorage.getItem("aura_token") || "")}`;

  return (
    <div data-testid="creator-documents" className="rounded-xl border border-aura-border bg-white/[0.02] p-4">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="font-display text-sm font-semibold text-white">{t("docs.title")}</h3>
          <p className="text-xs text-zinc-500">{t("docs.subtitle")}</p>
        </div>
        <button type="button" data-testid="doc-upload-btn" onClick={onPick} disabled={uploading}
          className="shrink-0 inline-flex items-center gap-2 rounded-full border border-white/20 text-xs text-zinc-200 hover:border-white/50 hover:text-white px-4 py-2 transition-colors disabled:opacity-50">
          {uploading ? <><Loader2 size={14} className="animate-spin" /> {t("docs.uploading")}</> : <><Upload size={14} /> {t("docs.upload")}</>}
        </button>
        <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.webp,.gif" className="hidden" data-testid="doc-file-input" onChange={onFile} />
      </div>

      {loading ? (
        <div className="py-6 text-center text-zinc-500"><Loader2 size={16} className="animate-spin inline" /></div>
      ) : docs.length === 0 ? (
        <p className="py-6 text-center text-xs text-zinc-600">{t("docs.empty")}</p>
      ) : (
        <ul className="space-y-2">
          {docs.map((d) => {
            const isImg = (d.content_type || "").startsWith("image/");
            return (
              <li key={d.id} data-testid={`doc-row-${d.id}`} className="flex items-center gap-3 rounded-lg border border-white/10 bg-white/[0.02] px-3 py-2.5">
                {isImg ? <ImageIcon size={18} className="text-zinc-400 shrink-0" /> : <FileText size={18} className="text-zinc-400 shrink-0" />}
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-zinc-100 truncate">{d.original_filename}</p>
                  <p className="text-xs text-zinc-500">{fmtDate(d.created_at)} · {fmtSize(d.size)}</p>
                </div>
                <a href={downloadUrl(d.id)} target="_blank" rel="noopener noreferrer" data-testid={`doc-download-${d.id}`}
                  className="shrink-0 rounded-lg border border-white/15 text-zinc-300 hover:text-white hover:border-white/40 p-2 transition-colors"><Download size={15} /></a>
                <button type="button" data-testid={`doc-delete-${d.id}`} onClick={() => remove(d.id)}
                  className="shrink-0 rounded-lg border border-red-500/30 text-red-400 hover:bg-red-500/10 p-2 transition-colors"><Trash2 size={15} /></button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
